import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import clsx from "clsx";
import omit from "lodash/omit";

import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContentText from "@material-ui/core/DialogContentText";
import Button from "@material-ui/core/Button";
import {
  XGrid,
  GridSelectionModel,
  type GridCellParams,
  type GridColumns,
} from "@material-ui/x-grid";

import DialogTitle from "src/components/DialogTitleWithClose";
import ConfirmDialog from "src/components/ConfirmDialog";
import { userIdSelector } from "src/store/system/selector";
import { updateInventoryItems } from "src/apiService/modules/inventories";
import {
  getPendingInventoryCount,
  fetchPendingInventoryPage,
  refreshPendingDuplicateMatches,
  clearPendingInventoryMatches,
  deletePendingInventoryDocuments,
  promotePendingInventory,
  updatePendingInventoryDocument,
  isPendingInventoryChangedError,
  type PendingInventory,
  type RefreshDuplicateMatchesResult,
} from "src/apiService/modules/pendingInventory";
import type { Inventory } from "src/interfaces/inventory.interface";
import { asDate } from "src/utils/date";
import valueGetter from "src/utils/valueGetter";
import {
  PendingInventoryGridHeaderHeight,
  usePendingInventoryGridStyles,
} from "src/pages/RootDialogs/pendingInventoryGridStyles";

import PendingInventoryImport from "src/pages/RootDialogs/PendingInventoryImport";
import PendingInventoryCheckerWalkThroughGate from "src/pages/RootDialogs/PendingInventoryProcess/CheckerWalkThrough";
import PendingInventoryImportWalkThroughGate from "src/pages/RootDialogs/PendingInventoryProcess/ImportWalkThrough";

const PageSize = 50;
const CheckerTitleRowHeight = 36;
const CheckerSkuRowHeight = 50;

function renderSkuTitle(params: GridCellParams) {
  const title = String(params.value ?? "");
  return (
    <span className="pending-inventory-sku-title" title={title}>
      {title}
    </span>
  );
}

const ColumnsTitle: GridColumns = [
  {
    valueGetter,
    field: "current.item_title",
    headerName: "Item Title",
    flex: 1,
  },
  {
    valueGetter,
    field: "current.list_date",
    headerName: "Duplicate List Date",
    description:
      "This is the list date of the item currently in inventory that matches the title of a new listing",
    type: "date",
    width: 150,
  },
  {
    valueGetter,
    field: "new.list_date",
    headerName: "New Item List Date",
    description:
      "This is the list date of the new listing that we received from the marketplace",
    type: "date",
    width: 150,
  },
  {
    valueGetter,
    field: "current.quantity",
    headerName: "Current Item Count",
    description:
      "This is the total number of items with this item title in your current inventory",
    width: 150,
  },
  {
    valueGetter,
    field: "new.quantity",
    headerName: "New Item Count",
    description:
      "This is the number of items being brought in with the same item title as an item in the inventory",
    width: 150,
  },
];

const ColumnsSku: GridColumns = [
  {
    valueGetter,
    field: "current.item_title",
    headerName: "Current Item Title",
    flex: 1,
    renderCell: renderSkuTitle,
  },
  {
    valueGetter,
    field: "new.item_title",
    headerName: "New Item Title",
    flex: 1,
    renderCell: renderSkuTitle,
  },
  ...ColumnsTitle.filter((c) => c.field !== "current.item_title").map((c) => {
    if (c.field === "current.list_date") {
      return {
        ...c,
        description:
          "This is the list date of the item currently in inventory that matches the SKU of a new listing.",
      };
    }
    if (c.field === "current.quantity") {
      return {
        ...c,
        description:
          "This is the total number of items with this sku in your current inventory.",
      };
    }
    if (c.field === "new.quantity") {
      return {
        ...c,
        description:
          "This is the number of items being brought in with the same sku as an item in your inventory.",
      };
    }
    return c;
  }),
];

type MatchKind = "title" | "sku";
type PendingEdit = Omit<Partial<PendingInventory>, "purchase_date"> & {
  purchase_date?: Date | null;
};

const SaveDelayMs = 500;

interface MatchRow {
  id: string;
  pendingId: string;
  timestamp?: number;
  current: {
    id: string;
    item_title: string;
    list_date: Date | null;
    quantity: number;
  };
  new: Inventory & { listing_id?: string; download_timestamp?: number };
}

const useStyles = makeStyles((theme) => ({
  content: {
    minHeight: "40vh",
  },
  checkerContent: {
    height: "80vh",
  },
  checkerGrid: {
    "& .pending-inventory-sku-title": {
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      whiteSpace: "normal",
      lineHeight: "normal",
      width: "100%",
    },
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

function toReviewRow(item: PendingInventory): Inventory {
  const nullKeys = Object.keys(item).filter((k) => item[k] === null);
  const cleaned = (nullKeys.length ? omit(item, nullKeys) : item) as Inventory &
    PendingInventory;
  const {
    matchType: _mt,
    matchedInventoryId: _id,
    matchedItemTitle: _title,
    matchedListDate: _ld,
    matchedQuantity: _qty,
    ...rest
  } = cleaned;
  return {
    ...rest,
    purchase_price: rest.purchase_price || 0,
    purchase_date: asDate(rest.purchase_date),
    list_date: asDate(rest.list_date) ?? null,
  };
}

function timestampMillis(value: unknown): number | null {
  if (value == null) return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === "object") {
    const candidate = value as {
      toMillis?: () => number;
      seconds?: number;
    };
    if (typeof candidate.toMillis === "function") {
      const ms = candidate.toMillis();
      return Number.isNaN(ms) ? null : ms;
    }
    if (typeof candidate.seconds === "number") {
      return candidate.seconds * 1000;
    }
  }
  if (typeof value === "string") {
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

/** Client-side no-op check; the backend repeats this authoritatively. */
function pendingPatchValueEqual(
  key: keyof PendingEdit,
  current: unknown,
  next: unknown
): boolean {
  if (key === "purchase_date") {
    return timestampMillis(current) === timestampMillis(next);
  }
  if (key === "purchase_price") {
    return (Number(current) || 0) === (Number(next) || 0);
  }
  if (key === "quantity") {
    return (Number(current) || 0) === (Number(next) || 0);
  }
  return String(current ?? "") === String(next ?? "");
}

function toMatchRow(item: PendingInventory): MatchRow | null {
  // Transition fallback: documents written before the single-id rename carry
  // the full array. Keep reading it until every environment is redeployed.
  const legacyIds = (
    item as PendingInventory & { matchedInventoryIds?: string[] }
  ).matchedInventoryIds;
  const matchedId = item.matchedInventoryId
    ? String(item.matchedInventoryId)
    : legacyIds?.length
    ? String(legacyIds[0])
    : "";
  if (!matchedId || !item.id) return null;
  return {
    id: item.id,
    pendingId: item.id,
    timestamp: item.timestamp,
    current: {
      id: matchedId,
      item_title: item.matchedItemTitle || "",
      list_date: asDate(item.matchedListDate as never) ?? null,
      quantity: item.matchedQuantity ?? 0,
    },
    new: {
      ...toReviewRow(item),
      listing_id: item.listing_id,
      download_timestamp: item.download_timestamp ?? item.timestamp,
    },
  };
}

export default function PendingInventoryProcess({
  onClose,
}: {
  onClose: (event?: unknown, reason?: string) => void;
}) {
  const classes = useStyles();
  const gridClasses = usePendingInventoryGridStyles();
  const uid = useSelector(userIdSelector);
  const [matchSummary, setMatchSummary] =
    useState<RefreshDuplicateMatchesResult | null>(null);
  const [matchRows, setMatchRows] = useState<MatchRow[]>([]);
  const matchRowsRef = useRef<MatchRow[]>([]);
  const [matchKind, setMatchKind] = useState<MatchKind | null>(null);
  const [matchHasMore, setMatchHasMore] = useState(false);
  const [matchLoadingMore, setMatchLoadingMore] = useState(false);
  const matchHasMoreRef = useRef(false);
  const matchLoadingMoreRef = useRef(false);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [phase, setPhase] = useState<
    "loading" | "checker" | "import" | "empty"
  >("loading");
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loadedItems, setLoadedItems] = useState<PendingInventory[]>([]);
  const loadedItemsRef = useRef<PendingInventory[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [idsToDelete, setIdsToDelete] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadingMoreRef = useRef(false);
  // Edits are local-first and saved on a short debounce. Add flushes every
  // queued edit before promotion so a quick edit -> Add cannot promote a
  // stale pending document.
  const saveTimersRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>()
  );
  const saveChainsRef = useRef(new Map<string, Promise<void>>());
  const dirtyPatchesRef = useRef(new Map<string, PendingEdit>());
  const flushPendingSaveRef = useRef<(id: string) => Promise<void>>(() =>
    Promise.resolve()
  );
  const staleReloadRef = useRef(false);
  const addInFlightRef = useRef(false);
  const saveGenerationRef = useRef(0);
  // Refs mirror paginated rows so scroll/load-more handlers avoid stale closures.
  const matchSummaryRef = useRef<RefreshDuplicateMatchesResult | null>(null);
  matchSummaryRef.current = matchSummary;

  async function openImport() {
    if (!uid) return;
    setPhase("import");
    setImportLoading(true);
    setMatchKind(null);
    matchRowsRef.current = [];
    setMatchRows([]);
    matchHasMoreRef.current = false;
    setMatchHasMore(false);
    loadedItemsRef.current = [];
    setLoadedItems([]);
    try {
      const count = await getPendingInventoryCount();
      setTotalCount(count);
      const { items, hasMore: more } = await fetchPendingInventoryPage({
        uid,
        pageSize: PageSize,
        matchType: "none",
      });
      loadedItemsRef.current = items;
      setLoadedItems(items);
      setHasMore(more);
      if (!items.length && !more) setPhase("empty");
    } catch (e) {
      setError((e as Error).message || String(e));
      toast.error("Failed to load pending inventory.");
      setPhase("empty");
    } finally {
      setImportLoading(false);
    }
  }

  async function loadMatchPage(kind: MatchKind, reset: boolean) {
    if (!uid) return { rows: [] as MatchRow[], hasMore: false };
    if (!reset && matchLoadingMoreRef.current) {
      return { rows: matchRowsRef.current, hasMore: matchHasMoreRef.current };
    }
    matchLoadingMoreRef.current = true;
    setMatchLoadingMore(true);
    if (reset) {
      matchRowsRef.current = [];
      setMatchRows([]);
    }
    try {
      const last = reset
        ? undefined
        : matchRowsRef.current[matchRowsRef.current.length - 1];
      const page = await fetchPendingInventoryPage({
        uid,
        pageSize: PageSize,
        matchType: kind,
        startAfterTimestamp: last?.timestamp,
        startAfterId: last?.id,
      });
      const rows = page.items.map(toMatchRow).filter(Boolean) as MatchRow[];
      const next = reset ? rows : [...matchRowsRef.current, ...rows];
      matchRowsRef.current = next;
      setMatchRows(next);
      matchHasMoreRef.current = page.hasMore;
      setMatchHasMore(page.hasMore);
      return { rows: next, hasMore: page.hasMore };
    } finally {
      matchLoadingMoreRef.current = false;
      setMatchLoadingMore(false);
    }
  }

  async function showMatchKind(
    kind: MatchKind | null,
    counts: RefreshDuplicateMatchesResult
  ) {
    if (!kind) {
      await openImport();
      return;
    }
    setMatchKind(kind);
    setSelectionModel([]);
    const loaded = await loadMatchPage(kind, true);
    if (!loaded.rows.length && !loaded.hasMore) {
      const followup: MatchKind | null =
        kind === "title" && counts.skuMatches > 0 ? "sku" : null;
      await showMatchKind(followup, counts);
      return;
    }
    setPhase("checker");
  }

  async function runBootstrap() {
    if (!uid) return;
    setPhase("loading");
    setError(null);
    setSelectionModel([]);
    try {
      const counts = await refreshPendingDuplicateMatches();
      setMatchSummary(counts);
      matchSummaryRef.current = counts;
      const firstKind: MatchKind | null =
        counts.matchType === "title" || counts.matchType === "sku"
          ? counts.matchType
          : counts.titleMatches > 0
          ? "title"
          : counts.skuMatches > 0
          ? "sku"
          : null;
      await showMatchKind(firstKind, counts);
    } catch (e) {
      setError((e as Error).message || String(e));
      toast.error("Failed to prepare pending inventory.");
      setPhase("empty");
    }
  }

  const runBootstrapRef = useRef(runBootstrap);
  runBootstrapRef.current = runBootstrap;

  const clearAllPendingSaves = () => {
    for (const timer of saveTimersRef.current.values()) {
      clearTimeout(timer);
    }
    saveTimersRef.current.clear();
    dirtyPatchesRef.current.clear();
    // Keep the per-row chains: an already-running write cannot be cancelled,
    // and newer edits must wait for it instead of racing it.
  };

  /**
   * A sync changed a row after this review started. Discard queued edits
   * (they are based on the old values), warn, and re-run the checker so the
   * client gets a new server reviewStartedAt and fresh match fields.
   */
  async function handleStaleReview(notify = true) {
    if (staleReloadRef.current) return;
    staleReloadRef.current = true;
    saveGenerationRef.current += 1;
    clearAllPendingSaves();
    setSelectionModel([]);
    setIdsToDelete([]);
    if (notify) {
      toast.warning(
        "Some pending inventory items changed during review. Unsaved edits were discarded and the list was refreshed."
      );
    }
    try {
      await runBootstrapRef.current();
    } finally {
      staleReloadRef.current = false;
    }
  }

  useEffect(() => {
    void runBootstrapRef.current();
  }, [uid]);

  async function loadImportPage(reset: boolean) {
    if (!uid) return;
    if (!reset && loadingMoreRef.current) return;
    if (reset) {
      setError(null);
    } else {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }
    try {
      if (reset) {
        const count = await getPendingInventoryCount();
        setTotalCount(count);
      }
      const last = reset
        ? undefined
        : loadedItemsRef.current[loadedItemsRef.current.length - 1];
      const { items, hasMore: more } = await fetchPendingInventoryPage({
        uid,
        pageSize: PageSize,
        matchType: "none",
        startAfterTimestamp: last?.timestamp,
        startAfterId: last?.id,
      });
      const next = reset ? items : [...loadedItemsRef.current, ...items];
      loadedItemsRef.current = next;
      setLoadedItems(next);
      setHasMore(more);
    } catch (e) {
      setError((e as Error).message || String(e));
      toast.error("Failed to load pending inventory.");
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }

  const clearSaveTimer = (id: string) => {
    const timer = saveTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      saveTimersRef.current.delete(id);
    }
  };

  const flushPendingSave = async (id: string): Promise<void> => {
    clearSaveTimer(id);
    if (!dirtyPatchesRef.current.has(id)) {
      return saveChainsRef.current.get(id) || Promise.resolve();
    }
    // Wait for the previous write for this row before reading the patch. A
    // failed previous write may re-add its fields; capturing after the wait
    // lets the newer patch supersede it instead of being overwritten later.
    const prev = saveChainsRef.current.get(id) || Promise.resolve();
    const next = prev
      // A newer patch supersedes an older failed write. If the old write was
      // stale, the new patch runs with the refreshed review timestamp; if it
      // is still stale, the new write reports it.
      .catch(() => undefined)
      .then(async () => {
        const patch = dirtyPatchesRef.current.get(id);
        if (!patch) return;
        const reviewStartedAt = matchSummaryRef.current?.reviewStartedAt;
        if (!reviewStartedAt) {
          throw new Error("Pending inventory review is not ready.");
        }
        const generation = saveGenerationRef.current;
        dirtyPatchesRef.current.delete(id);
        try {
          await updatePendingInventoryDocument(
            id,
            patch as Record<string, unknown>,
            reviewStartedAt
          );
        } catch (error) {
          // A stale row needs a reload with a new review timestamp; retrying
          // this patch would just fail again. Other errors are transient and
          // keep the edit for the next attempt.
          if (isPendingInventoryChangedError(error)) throw error;
          if (saveGenerationRef.current === generation) {
            const newer = dirtyPatchesRef.current.get(id) || {};
            dirtyPatchesRef.current.set(id, { ...patch, ...newer });
          }
          throw error;
        }
      });
    saveChainsRef.current.set(id, next);
    const clearSettled = () => {
      if (saveChainsRef.current.get(id) === next) {
        saveChainsRef.current.delete(id);
      }
    };
    void next.then(clearSettled, clearSettled);
    return next;
  };
  flushPendingSaveRef.current = flushPendingSave;

  // The outer Dialog owns backdrop/Escape closes, so it can unmount this
  // component without calling handleClose. Fire any remaining debounced edits
  // rather than losing them.
  useEffect(() => {
    const dirtyPatches = dirtyPatchesRef.current;
    return () => {
      for (const id of dirtyPatches.keys()) {
        void flushPendingSaveRef.current(id).catch(() => undefined);
      }
    };
  }, []);

  const schedulePendingSave = (id: string) => {
    clearSaveTimer(id);
    saveTimersRef.current.set(
      id,
      setTimeout(() => {
        saveTimersRef.current.delete(id);
        const generation = saveGenerationRef.current;
        void flushPendingSave(id).catch((e) => {
          if (saveGenerationRef.current !== generation) return;
          if (isPendingInventoryChangedError(e)) {
            void handleStaleReview();
          } else {
            toast.error(`Failed to save edit: ${(e as Error).message}`);
          }
        });
      }, SaveDelayMs)
    );
  };

  const flushPendingSaves = async () => {
    const targets = [
      ...new Set([
        ...dirtyPatchesRef.current.keys(),
        ...saveChainsRef.current.keys(),
      ]),
    ];
    await Promise.all(
      targets.map(async (id) => {
        for (;;) {
          await flushPendingSave(id);
          if (!dirtyPatchesRef.current.has(id)) return;
        }
      })
    );
  };

  const cancelPendingSaves = (ids: string[]) => {
    for (const id of ids) {
      clearSaveTimer(id);
      dirtyPatchesRef.current.delete(id);
      saveChainsRef.current.delete(id);
    }
  };

  const handleClose = (event?: unknown, reason?: string) => {
    // Add owns the dialog until its flush + promote sequence finishes.
    if (addInFlightRef.current) return;
    void flushPendingSaves()
      .catch((e) => {
        if (isPendingInventoryChangedError(e)) {
          toast.warning(
            "Some pending inventory items changed during review. Unsaved edits were not applied."
          );
        } else {
          toast.error(`Failed to save edit: ${(e as Error).message}`);
        }
      })
      .finally(() => onClose(event, reason));
  };

  const patchPendingRow = (id: string, patch: PendingEdit) => {
    if (addInFlightRef.current || staleReloadRef.current) return;
    const current = loadedItemsRef.current.find((item) => item.id === id);
    const effective: PendingEdit = {};
    for (const [key, value] of Object.entries(patch) as [
      keyof PendingEdit,
      unknown
    ][]) {
      // Firestore snapshot dates arrive as Timestamp objects. The update
      // callable validates purchase_date as a date, and a Timestamp serializes
      // to `{ seconds, nanoseconds }` over the callable wire, so normalize it
      // here (this also covers the "copy previous row" action).
      const nextValue = key === "purchase_date" ? asDate(value) ?? null : value;
      const currentValue = (current as Record<string, unknown> | undefined)?.[
        key as string
      ];
      if (current && pendingPatchValueEqual(key, currentValue, nextValue)) {
        continue;
      }
      (effective as Record<string, unknown>)[key as string] = nextValue;
    }
    if (!Object.keys(effective).length) return;

    const next = loadedItemsRef.current.map((item) =>
      item.id === id ? ({ ...item, ...effective } as PendingInventory) : item
    );
    loadedItemsRef.current = next;
    setLoadedItems(next);
    dirtyPatchesRef.current.set(id, {
      ...(dirtyPatchesRef.current.get(id) || {}),
      ...effective,
    });
    schedulePendingSave(id);
  };

  const afterMatchRowsChange = async (remaining: MatchRow[]) => {
    matchRowsRef.current = remaining;
    setMatchRows(remaining);
    setSelectionModel([]);
    if (remaining.length) return;
    const kind = matchKind;
    if (matchHasMoreRef.current && kind) {
      const loaded = await loadMatchPage(kind, true);
      if (loaded.rows.length || loaded.hasMore) return;
    }
    const summary = matchSummaryRef.current;
    if (!summary) return;
    const followup: MatchKind | null =
      kind === "title" && summary.skuMatches > 0 ? "sku" : null;
    await showMatchKind(followup, summary);
  };

  const handleRemoveFromImport = async () => {
    if (!selectionModel.length || busy) return;
    setBusy(true);
    try {
      const selected = matchRows.filter((m) => selectionModel.includes(m.id));
      const updates = selected
        .filter((m) => m.current.id && m.new.listing_id)
        .map((m) => ({
          id: m.current.id,
          listing_id: String(m.new.listing_id),
        }));
      const pendingIds = selected.map((m) => m.pendingId);
      if (updates.length) await updateInventoryItems(updates);
      cancelPendingSaves(pendingIds);
      await deletePendingInventoryDocuments(pendingIds);
      const remaining = matchRows.filter((m) => !selectionModel.includes(m.id));
      setTotalCount((c) =>
        c == null ? c : Math.max(0, c - pendingIds.length)
      );
      await afterMatchRowsChange(remaining);
    } catch (e) {
      toast.error(`Failed to remove from import: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleAddToImport = async () => {
    if (!selectionModel.length || busy) return;
    setBusy(true);
    try {
      const selected = matchRows.filter((m) => selectionModel.includes(m.id));
      await clearPendingInventoryMatches(selected.map((m) => m.pendingId));
      const remaining = matchRows.filter((m) => !selectionModel.includes(m.id));
      await afterMatchRowsChange(remaining);
    } catch (e) {
      toast.error(`Failed to add to import: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  if (phase === "loading") {
    return (
      <>
        <DialogTitle onClose={handleClose}>Pending Inventory</DialogTitle>
        <DialogContent className={classes.content}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={6}
            aria-busy
            aria-label="Loading pending inventory"
          >
            <CircularProgress size={32} />
          </Box>
        </DialogContent>
      </>
    );
  }

  if (error && phase !== "checker" && phase !== "import") {
    return (
      <>
        <DialogTitle onClose={handleClose}>Pending Inventory</DialogTitle>
        <DialogContent className={classes.content}>
          <DialogContentText>
            We couldn’t load your pending inventory. Please try again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={() => void runBootstrapRef.current()}
          >
            Retry
          </Button>
          <Button color="secondary" variant="contained" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </>
    );
  }

  if (phase === "empty") {
    return (
      <>
        <DialogTitle onClose={handleClose}>Pending Inventory</DialogTitle>
        <DialogContent className={classes.content}>
          <DialogContentText>
            There are no pending inventory items to review.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </>
    );
  }

  if (phase === "checker") {
    const Columns = (matchKind === "sku" ? ColumnsSku : ColumnsTitle).map(
      (column) => ({
        ...column,
        sortable: false,
        filterable: false,
      })
    );
    return (
      <>
        <PendingInventoryCheckerWalkThroughGate
          loading={busy || matchLoadingMore}
        />
        <DialogTitle onClose={handleClose}>
          Duplicate Inventory Checker{" "}
          {matchKind === "title" ? "(Title Matches)" : "(SKU Matches)"}
        </DialogTitle>
        <DialogContent
          dividers
          id="pending-inventory-checker-grid"
          className={clsx(gridClasses.content, classes.checkerContent)}
        >
          <XGrid
            className={clsx(gridClasses.grid, classes.checkerGrid)}
            selectionModel={selectionModel}
            onSelectionModelChange={(sm) => setSelectionModel(sm)}
            checkboxSelection
            disableSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            hideFooter
            columns={Columns}
            rows={matchRows}
            loading={busy || matchLoadingMore}
            density="standard"
            rowHeight={
              matchKind === "sku" ? CheckerSkuRowHeight : CheckerTitleRowHeight
            }
            headerHeight={PendingInventoryGridHeaderHeight}
            scrollEndThreshold={200}
            onRowsScrollEnd={() => {
              if (
                !matchKind ||
                !matchHasMore ||
                matchLoadingMoreRef.current ||
                busy
              )
                return;
              void loadMatchPage(matchKind, false);
            }}
          />
        </DialogContent>
        <DialogActions>
          <div
            id="duplicate-inventory-checker-actions"
            className="MuiDialogActions-spacing"
          >
            <Button
              className={classes.deleteButton}
              color="secondary"
              variant="contained"
              disabled={busy || selectionModel.length === 0}
              onClick={() => void handleRemoveFromImport()}
            >
              Remove from Import
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => void handleAddToImport()}
              disabled={busy || selectionModel.length === 0}
            >
              Add to Import
            </Button>
          </div>
        </DialogActions>
      </>
    );
  }

  // import phase
  return (
    <>
      <PendingInventoryImportWalkThroughGate
        loading={importLoading || loadingMore}
      />
      <ConfirmDialog
        open={idsToDelete.length > 0}
        title="Confirm"
        text={
          idsToDelete.length === 1
            ? "Are you sure you want to delete 1 pending inventory item?"
            : `Are you sure you want to delete ${idsToDelete.length} pending inventory items?`
        }
        confirm="Delete"
        onCancel={() => setIdsToDelete([])}
        onConfirm={async () => {
          const ids = idsToDelete;
          cancelPendingSaves(ids);
          await deletePendingInventoryDocuments(ids);
          const idSet = new Set(ids);
          const next = loadedItemsRef.current.filter(
            (item) => !item.id || !idSet.has(item.id)
          );
          loadedItemsRef.current = next;
          setLoadedItems(next);
          setTotalCount((count) =>
            count == null ? count : Math.max(0, count - ids.length)
          );
          setIdsToDelete([]);
          toast.success(
            ids.length === 1
              ? "Selected item was removed."
              : "Selected items were removed."
          );
          if (!next.length && !hasMore) handleClose();
        }}
      />

      <PendingInventoryImport
        rows={loadedItems}
        loading={importLoading || loadingMore}
        hasMore={hasMore}
        loadedCount={loadedItems.length}
        totalCount={totalCount}
        onLoadMore={() => void loadImportPage(false)}
        onClose={handleClose}
        onPatch={patchPendingRow}
        onDelete={(ids) => setIdsToDelete(ids)}
        onAdd={async (ids, options) => {
          if (addInFlightRef.current) return;
          addInFlightRef.current = true;
          try {
            const reviewStartedAt = matchSummaryRef.current?.reviewStartedAt;
            if (!reviewStartedAt) {
              throw new Error("Pending inventory review is not ready.");
            }
            // Add is a boundary. The import dialog switches to its loading
            // state and locks the grid, and every queued edit is flushed
            // before promotion reads the pending documents. Flushing only the
            // selected rows would let edits on other rows land after Add.
            await flushPendingSaves();
            const result = await promotePendingInventory(ids, reviewStartedAt);
            if (result.changed.length) {
              toast.warning(
                result.imported
                  ? `Imported ${result.imported}. ${result.changed.length} changed during review and were kept.`
                  : `${result.changed.length} ${
                      result.changed.length === 1 ? "item" : "items"
                    } changed during review and were not imported.`
              );
              await handleStaleReview(false);
              return;
            }
            toast.success(
              result.skipped
                ? `Imported ${result.imported}. ${result.skipped} no longer in pending inventory.`
                : options?.close === false
                ? `Imported ${result.imported}.`
                : "Inventory imported successfully."
            );
            // The import screen keeps the dialog open when some selected rows
            // still have quantity 0, so only the promoted rows are removed.
            if (options?.close === false) {
              const idSet = new Set(ids);
              const next = loadedItemsRef.current.filter(
                (item) => !item.id || !idSet.has(item.id)
              );
              loadedItemsRef.current = next;
              setLoadedItems(next);
              setTotalCount((count) =>
                count == null ? count : Math.max(0, count - result.imported)
              );
              return;
            }
            // No edits are accepted after Add is dispatched, and the pre-Add
            // flush emptied the queue, so there is nothing left to persist.
            onClose();
          } catch (e) {
            if (isPendingInventoryChangedError(e)) {
              await handleStaleReview();
            } else {
              toast.error(`There was an error: ${(e as Error).message}`);
            }
            throw e;
          } finally {
            addInFlightRef.current = false;
          }
        }}
      />
    </>
  );
}
